import { isValidPhoneNumber } from 'react-phone-number-input';
import z from 'zod';

import type { FileDto } from '@/api/api.dto';
import type { List } from '@/utils/list';

export enum CrewVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export enum RateType {
  HOUR = 'hour',
  DAY = 'day',
  MONTH = 'month',
}

export enum EducationDegree {
  PHD = 'PHD',
  BACHELOR = 'bachelor',
}

export interface RateAmount {
  number: number;
  currency: string;
}

/**
 * The rate per period as the API keeps it. A profile states one — the period the person is paid
 * by — and leaves the other two null; older profiles may still carry several, or zeros. A
 * negotiable rate states no amount at all.
 */
export interface CrewRate {
  hour: RateAmount | null;
  day: RateAmount | null;
  month: RateAmount | null;
  negotiable: boolean;
}

type RateAmountDto = {
  number: string;
  currency: string;
} | null;

export interface CrewRateDto {
  hour: RateAmountDto;
  day: RateAmountDto;
  month: RateAmountDto;
  /** Absent on profiles saved before the option existed. */
  negotiable?: boolean;
}

export interface CrewLocationDto {
  name: string;
  address: string;
  city: string;
  state: string;
  place_id: unknown;
  zip_code: string;
  latitude: number;
  longitude: number;
}

export interface CrewEducationDto {
  degree: EducationDegree;
  description: string;
  end: string;
  id: string;
  profile_id: string;
  start: string;
  university_name: string;
}

export enum EmploymentType {
  TEMPORARY = 'temporary',
  PERMANENT = 'permanent',
}

export interface CrewExperienceDto {
  description: string;
  employment_type: EmploymentType;
  end: string;
  id: string;
  profile_id: string;
  reference: unknown;
  references_id: unknown;
  roles: unknown[];
  start: string;
  title: string;
  yacht_info: string;
  recommendation_file?: null | CrewFileDto;
}

export enum CrewPosition {
  CAPTAIN = 'captain',
  MATE_DECKHAND = 'mate-deckhand',
  STEWARD = 'steward',
  CHEF = 'chef',
}

export interface CrewFiltersDto {
  search?: string;
  starred?: boolean;
  city?: string;
  rate_type?: string;
  rate_min?: number;
  rate_max?: number;
  skills?: string[];
  experience_years?: number;
  roles?: string[];
  open_to_temporary?: boolean;
}

export interface CrewFileDto {
  name: string;
  link: string;
  content_type: string;
  size: number;
}

export type PreferredContactMethod = 'call' | 'chat' | 'email' | 'whatsapp';

export const PREFERRED_CONTACT_METHOD_LABELS: Record<PreferredContactMethod, string> = {
  call: 'Call',
  chat: 'Chat',
  email: 'Email',
  whatsapp: 'WhatsApp',
};

/** The kind of contract a job seeker is after — `people.work_type` on the backend. */
export type WorkType = 'permanent' | 'seasonal' | 'rotation' | 'daywork' | 'delivery';

export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  permanent: 'Permanent',
  seasonal: 'Seasonal',
  rotation: 'Rotation',
  daywork: 'Daywork',
  delivery: 'Delivery',
};

/** Every work type but permanent also makes a candidate open to temporary contracts. */
export const WORK_TYPE_OPTIONS = (Object.keys(WORK_TYPE_LABELS) as WorkType[]).map((value) => ({
  label: WORK_TYPE_LABELS[value],
  value,
}));

/** The work types as a CV reads them, e.g. `Seasonal, Daywork`; empty when none is chosen. */
export const formatWorkTypes = (workTypes: WorkType[] | undefined): string =>
  (workTypes ?? []).map((type) => WORK_TYPE_LABELS[type]).join(', ');

export interface SingleCrewDto {
  person_id: string;
  first_name: string;
  last_name: string;
  gender: string;
  professional_profile: string;
  preferred_contact_method?: PreferredContactMethod | null;
  visibility: CrewVisibility;
  positions: CrewPosition[];
  rate: CrewRateDto | null;
  years_of_experience: number;
  email: string | null;
  phone_number: string;
  work_types?: WorkType[];
  /** RFC 3339 midnight UTC of the day, e.g. `2026-10-20T00:00:00Z`. */
  available_from?: string | null;
  /** Takes temporary contracts; absent on profiles saved before the option existed. */
  open_to_temporary?: boolean;
  location: CrewLocationDto;
  smoker: boolean;
  tattoos: boolean;
  has_driver_licence: boolean;
  photo?: FileDto | null;
  skills: string[];
  licence: string[];
  cruising_experience: string[];
  created_at: string;
  updated_at: string;
  experiences: CrewExperienceDto[];
  educations: CrewEducationDto[];
  recommendation_files: unknown[];
  cv?: null | CrewFileDto;
  liked: boolean;
  years_of_experience_calculated?: null | number;
}

export type CrewListDto = List<SingleCrewDto>;

export interface CrewLocation {
  name: string;
  address: string;
  city: string;
  state: string;
  placeId: unknown;
  zipCode: string;
  latitude: number;
  longitude: number;
}

export interface CrewEducation {
  degree: EducationDegree;
  description: string;
  end: string;
  id: string;
  profileId: string;
  start: string;
  universityName: string;
}

export interface CrewExperience {
  description: string;
  employmentType: EmploymentType;
  end: string;
  id: string;
  profileId: string;
  reference: unknown;
  referencesId: unknown;
  roles: unknown[];
  start: string;
  title: string[];
  yachtInfo: string;
  recommendationFile?: null | CrewFile;
}

export interface CrewFilters {
  search: string;
  isFavorite: boolean;
  city: string;
  rateType: RateType;
  rateMin: number;
  rateMax: number;
  skills: string[];
  experienceYears: number;
  positions: string[];
  openToTemporary: boolean;
}

export interface CrewFile {
  name: string;
  link: string;
  contentType: string;
  size: number;
}

export interface SingleCrew {
  personId: string;
  firstName: string;
  lastName: string;
  gender: string;
  professionalProfile: string;
  preferredContactMethod?: PreferredContactMethod | null;
  visibility: CrewVisibility;
  positions: CrewPosition[];
  rate: CrewRate;
  yearsOfExperience?: null | number;
  yearsOfExperienceCalculated?: null | number;
  email: string;
  phoneNumber: string;
  workTypes: WorkType[];
  availableFrom: string | null;
  openToTemporary: boolean;
  location: CrewLocation;
  smoker: boolean;
  tattoos: boolean;
  hasDriverLicence: boolean;
  photo?: null | string;
  skills: string[];
  licence: string[];
  cruisingExperience: string[];
  createdAt: string;
  updatedAt: string;
  experiences: CrewExperience[];
  educations: CrewEducation[];
  recommendationFiles: unknown[];
  cv?: null | CrewFile;
  isFavorite: boolean;
}

export type CrewList = List<SingleCrew>;

export interface UpdateProfileDto {
  cruising_experience: string[];
  /** Null when the account has none — a phone-registered crew member finishing onboarding. */
  email: string | null;
  positions: string[];
  skills: string[];
  visibility: string;
  has_driver_licence: boolean;
  licence: string[];
  location: CrewLocationDto;
  /** Null when the account has none — an email-registered crew member finishing onboarding. */
  phone_number: string | null;
  professional_profile: string;
  preferred_contact_method?: PreferredContactMethod | null;
  work_types: WorkType[];
  available_from: string | null;
  rate: CrewRateDto | null;
  smoker: boolean;
  tattoos: boolean;
  years_of_experience?: null | number;
}

export const createUpdateProfileSchema = z.object({
  positions: z.array(z.string()).min(1, 'Please choose at least one position.'),
  cruisingExperience: z.array(z.string()),
  email: z.string().email('Please enter a valid email address.'),
  skills: z.array(z.string()),
  visibility: z.enum(['public', 'private']),
  hasDriverLicence: z.boolean(),
  licence: z.array(z.string()),
  location: z.object({
    name: z.string(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    placeId: z.unknown(),
    zipCode: z.string(),
    latitude: z.number(),
    longitude: z.number(),
  }),
  phoneNumber: z
    .string()
    .min(1, 'Please enter a mobile number.')
    .refine((value) => isValidPhoneNumber(value), {
      message: 'Please enter a valid mobile number.',
    }),
  professionalProfile: z.string(),
  preferredContactMethod: z.enum(['call', 'chat', 'email', 'whatsapp']).nullish(),
  // Not edited by the CV forms, but carried through them: PUT /profiles replaces the whole
  // profile, so a field the form drops is a field the save wipes.
  workTypes: z
    .array(z.enum(['permanent', 'seasonal', 'rotation', 'daywork', 'delivery']))
    .optional(),
  availableFrom: z.string().nullish(),
  openToTemporary: z.boolean().optional(),
  // One rate and the period it is per; the mapper writes it to that period and clears the others.
  // Negotiable states no amount: the mapper then clears all three.
  rate: z.object({
    number: z
      .number('Please enter the rate as a number.')
      .gte(0, 'Please enter a rate of 0 or more.')
      .nullable(),
    currency: z.string(),
    period: z.enum(RateType),
    negotiable: z.boolean(),
  }),
  smoker: z.boolean(),
  tattoos: z.boolean(),
  yearsOfExperience: z
    .number('Please enter the years of experience as a number.')
    .nullable()
    .optional(),
  gender: z.string().nullable().optional(),
  firstName: z.string().min(1, 'Please enter a first name.'),
  lastName: z.string().min(1, 'Please enter a last name.'),
});

export type CreateUpdateProfile = z.infer<typeof createUpdateProfileSchema>;

export interface CreateEducationDto {
  degree: string;
  start: string;
  university_name: string;
  description: string;
  end: string;
}

export const createEducationSchema = z.object({
  personId: z.string(),
  degree: z.enum(EducationDegree, 'Please enter a degree.'),
  start: z.string().min(1, 'Please choose a start date.'),
  end: z.string().min(1, 'Please choose an end date.'),
  universityName: z.string().min(1, 'Please enter the university name.'),
  description: z.string(),
});

export type CreateEducation = z.infer<typeof createEducationSchema>;

export interface CruisingExperience {
  id: string;
  title: string;
}

export interface CrewSkill {
  id: string;
  title: string;
}

export interface CreateExperienceDto {
  employment_type: string;
  end: string;
  roles: string[];
  start: string;
  title: string;
  yacht_info: string;
  description: string;
  references_id?: null | string;
  file?: null | File;
}

export const createExperienceSchema = z.object({
  personId: z.string(),
  employmentType: z.enum(EmploymentType),
  start: z.string().min(1, 'Please choose a start date.'),
  end: z.string().min(1, 'Please choose an end date.'),
  roles: z.array(z.string()),
  title: z.array(z.string()).min(1, 'Please enter a job title.'),
  yachtInfo: z.string().min(1, 'Please enter the vessel details.'),
  description: z.string(),
  file: z.instanceof(File).nullable(),
});

export type CreateExperience = z.infer<typeof createExperienceSchema>;
